import { ProductForSaleDTO, ProductGroupForSaleDTO, ProductGroupType, SearchMode, TicketDTO } from 'core/api';
import { SessionProvider } from 'core/infrastructure';
import { CatalogueFacade, RestaurantFacade } from 'pos/facades';

export type ProductsSelection = {
	readonly groups: ProductGroupForSaleDTO[],
	readonly products: ProductForSaleDTO[],
	readonly sortMode: SortMode,
	readonly selectedGroup: Nullable<ProductGroupForSaleDTO>,
	readonly selectedSubgroup: Nullable<ProductForSaleDTO>,
	readonly searchScreenMode: SearchMode,
	readonly searchText: string,
	readonly showOnlySaleableProducts?: boolean,
	readonly showOnlyThisPosProducts?: boolean
}

export enum SortMode {
	SaleFormatName,
	Family,
	Barcode,
	PriceLookUpCode,
	Price
}
export type InitialSelection = { readonly selectedGroup: ProductGroupForSaleDTO, readonly products: ProductForSaleDTO[] }

export function createInitialPhoneState(groups: ProductGroupForSaleDTO[], initialSelection?: InitialSelection): ProductsSelection {
	return {
		groups: groups,
		products: initialSelection?.products ?? [],
		sortMode: SortMode.SaleFormatName,
		selectedGroup: initialSelection?.selectedGroup ?? null,
		selectedSubgroup: null,
		searchScreenMode: SearchMode.ProductGroup,
		searchText: '',

	};
}

export function createInitialForPos(groups: ProductGroupForSaleDTO[], expandTicketOnCreation: boolean, initialSelection?: InitialSelection): ProductsSelection {
	return {
		groups: groups,
		products: initialSelection?.products ?? [],
		sortMode: SortMode.SaleFormatName,
		selectedGroup: initialSelection?.selectedGroup ?? null,
		selectedSubgroup: null,
		searchScreenMode: SessionProvider.getCurrent().SearchMode,
		searchText: '',
	};
}

export async function selectGroup<State extends ProductsSelection>(state: State, group: ProductGroupForSaleDTO, ticketId: number, deps?: {
	getSaleableAsMainByProductGroup: typeof CatalogueFacade.getSaleableAsMainByProductGroup
}): Promise<State> {

	const newState: State = { ...state };

	if (group.Type === ProductGroupType.ProductForDirectSale)
		return newState;

	const products = await (deps?.getSaleableAsMainByProductGroup ?? CatalogueFacade.getSaleableAsMainByProductGroup)(group.Type, group.Id, ticketId);
	return {
		...newState,
		selectedGroup: group,
		selectedSubgroup: null,
		products,
	};
}


export async function searchProductsByText<State extends ProductsSelection>(state: State, text: string, ticketId: number, sortMode: SortMode, showOnlySaleableProducts?: boolean, showOnlyThisPosProducts?: boolean, deps?: {
	searchProductByText: typeof CatalogueFacade.searchProductByText
}): Promise<State> {
	if (text.length >= 3) {
		const { products } = await (deps?.searchProductByText ?? CatalogueFacade.searchProductByText)(ticketId, text, showOnlyThisPosProducts ?? true);

		const newState = {
			...state,
			searchText: text,
			showOnlyThisPosProducts,
			showOnlySaleableProducts,
			products: showOnlySaleableProducts ? products.filter(product => product.CannotBeSold === false) : products,
		};
		return sortProducts(newState, sortMode);
	}

	return clearProducts(state);
}

export function clearProducts<State extends ProductsSelection>(state: State): State {
	return { ...state, searchText: '', products: [] };
}

export function sortProducts<State extends ProductsSelection>(state: State, sortMode: SortMode): State {
	return {
		...state,
		sortMode,
		products: state.products.sort((a, b) => {
			switch (sortMode) {
				case SortMode.SaleFormatName:
					return a.SaleFormatName.localeCompare(b.SaleFormatName);
				case SortMode.Family:
					return a.FamilyName.localeCompare(b.FamilyName);
				case SortMode.Barcode: {
					// Si no tiene código de barras, se considera que va al final, por lo que al compararlo con otro producto 
					// con código de barras, el producto sin código de barras debería situarse después del producto con código de barras.
					if (a.Barcode === '' && b.Barcode !== '')
						return 1;
					if (a.Barcode !== '' && b.Barcode === '')
						return -1;

					return a.Barcode.localeCompare(b.Barcode);
				}
				case SortMode.PriceLookUpCode:
					// Si no tiene PLU, se considera que va al final.
					if (a.PriceLookUpCode === '' && b.PriceLookUpCode !== '')
						return 1;
					if (a.PriceLookUpCode !== '' && b.PriceLookUpCode === '')
						return -1;

					return a.PriceLookUpCode.localeCompare(b.PriceLookUpCode);
				case SortMode.Price:
					// Si no tiene precio, se considera que va al final, por lo que al compararlo con otro producto con precio 0,
					// el producto sin precio debería situarse antes que el producto con precio 0.
					return (a.Price ?? -1) - (b.Price ?? -1);
			}
		})
	};
}

export async function selectSubGroup<State extends ProductsSelection>(state: State, subgroup: ProductForSaleDTO, ticketId: number, deps?: {
	getSaleableAsMainByProductGroup: typeof CatalogueFacade.getSaleableAsMainByProductGroup
}): Promise<State> {

	const newState: State = { ...state };

	const type = subgroup.IsSubFamily ? ProductGroupType.Family : ProductGroupType.Category;
	const products = await (deps?.getSaleableAsMainByProductGroup ?? CatalogueFacade.getSaleableAsMainByProductGroup)(type, subgroup.Id, ticketId);
	return {
		...newState,
		selectedSubgroup: subgroup,
		products,
	};
}

export async function updateOnTicketChanged<State extends ProductsSelection>(state: State, oldTicket: Nullable<TicketDTO>, newTicket: TicketDTO, deps?: {
	getProductGroups: typeof CatalogueFacade.getProductGroups,
	getSaleableAsMainByProductGroup: typeof CatalogueFacade.getSaleableAsMainByProductGroup,
	getSaleCenterDefaultProductGroup: typeof RestaurantFacade.getSaleCenterDefaultProductGroup,
	searchProductByText: typeof CatalogueFacade.searchProductByText,
}): Promise<State> {

	const groups = await getProductGroups(state, oldTicket, newTicket, deps?.getProductGroups ?? CatalogueFacade.getProductGroups);
	const selection = await getGroupSelection(state, oldTicket, newTicket, deps?.getSaleCenterDefaultProductGroup ?? RestaurantFacade.getSaleCenterDefaultProductGroup);
	const products = await getProducts(state, oldTicket, newTicket, selection, deps?.getSaleableAsMainByProductGroup ?? CatalogueFacade.getSaleableAsMainByProductGroup, deps?.searchProductByText ?? CatalogueFacade.searchProductByText);

	return { ...state, groups, ...selection, products };
}

export function clearGroupSelection<State extends ProductsSelection>(state: State): State {
	return { ...state, selectedGroup: null, selectedSubgroup: null, products: [] };
}

export async function updateOnPricesChanged(state: ProductsSelection, ticketId: number, deps?: {
	getSaleableAsMainByProductGroup: typeof CatalogueFacade.getSaleableAsMainByProductGroup,
	searchProductByText: typeof CatalogueFacade.searchProductByText

}): Promise<ProductsSelection> {
	if (state.searchScreenMode === SearchMode.ProductGroup && state.selectedGroup)
		return await selectGroup(state, state.selectedGroup, ticketId, deps);

	if (state.searchScreenMode === SearchMode.Text)
		return await searchProductsByText(state, state.searchText, ticketId, state.sortMode, state.showOnlySaleableProducts, state.showOnlyThisPosProducts, deps);

	return state;
}

async function getProducts<State extends ProductsSelection>(state: State, oldTicket: Nullable<TicketDTO>, newTicket: TicketDTO, newSelection: GroupSelection, getSaleableAsMainByProductGroup: typeof CatalogueFacade.getSaleableAsMainByProductGroup, searchProductByText: typeof CatalogueFacade.searchProductByText): Promise<ProductForSaleDTO[]> {

	if (!saleCenterChanged(oldTicket, newTicket) && !ticketIdChanged(oldTicket, newTicket) && !priceListChanged(oldTicket, newTicket))
		return state.products;

	if (state.searchScreenMode === SearchMode.Text) {
		const { products }: { products: ProductForSaleDTO[] } = state.searchText === '' ? { products: [] } : await searchProductByText(newTicket.Id, state.searchText, state.showOnlyThisPosProducts ?? true);
		return state.showOnlySaleableProducts ? products.filter(product => product.CannotBeSold === false) : products;
	}

	if (newSelection.selectedGroup === null)
		return [];

	if (newSelection.selectedSubgroup !== null) {
		const groupType = newSelection.selectedSubgroup.IsSubCategory ? ProductGroupType.Category : ProductGroupType.Family;
		const groupId = newSelection.selectedSubgroup.Id;
		return await getSaleableAsMainByProductGroup(groupType, groupId, newTicket.Id);
	}

	return await getSaleableAsMainByProductGroup(newSelection.selectedGroup.Type, newSelection.selectedGroup.Id, newTicket.Id);
}

async function getProductGroups<State extends ProductsSelection>(state: State, oldTicket: Nullable<TicketDTO>, newTicket: TicketDTO, getProductGroups: typeof CatalogueFacade.getProductGroups): Promise<ProductGroupForSaleDTO[]> {
	return saleCenterChanged(oldTicket, newTicket) ? await getProductGroups(newTicket.Location.SaleCenterId) : state.groups;
}

type GroupSelection = {
	selectedGroup: Nullable<ProductGroupForSaleDTO>,
	selectedSubgroup: Nullable<ProductForSaleDTO>
}

async function getGroupSelection<State extends ProductsSelection>(state: State, oldTicket: Nullable<TicketDTO>, newTicket: TicketDTO, getSaleCenterDefaultProductGroup: typeof RestaurantFacade.getSaleCenterDefaultProductGroup): Promise<GroupSelection> {

	if (saleCenterChanged(oldTicket, newTicket) || ticketIdChanged(oldTicket, newTicket)) {
		const result = await getSaleCenterDefaultProductGroup(newTicket.Location.SaleCenterId);
		return { selectedGroup: result.HasDefaultProductGroup ? result.DefaultProductGroup : null, selectedSubgroup: null };
	}

	return { selectedGroup: state.selectedGroup, selectedSubgroup: state.selectedSubgroup };
}

function saleCenterChanged(oldTicket: Nullable<TicketDTO>, newTicket: TicketDTO): boolean {
	return oldTicket?.Location.SaleCenterId !== newTicket.Location.SaleCenterId;
}

function ticketIdChanged(oldTicket: Nullable<TicketDTO>, newTicket: TicketDTO): boolean {
	return oldTicket?.Id !== newTicket.Id;
}

function priceListChanged(oldTicket: Nullable<TicketDTO>, newTicket: TicketDTO): boolean {
	return oldTicket?.PriceList.Id !== newTicket.PriceList.Id;
}


